import { useState } from "react";
import { Table, Button, Modal, Form, Input, InputNumber, Select, Popconfirm, message, Space, Tag, DatePicker, Divider } from "antd";
import { Plus } from "lucide-react";
import dayjs from "dayjs";
import { useCertTypes, useCertTypeCreate, useCertTypeUpdate, useCertTypeDelete, useWorkerCerts, useWorkerCertCreate, useWorkerCertUpdate, useWorkerCertDelete, useCCUsers } from "../../../../api-hooks/compliance";

const STATUS_COLOR: Record<string, string> = { active: "green", expired: "red", expiring_soon: "orange", revoked: "default" };

export default function CertificationsTab({ cid }: { cid: number }) {
  const [msgApi, ctx] = message.useMessage();
  const [form] = Form.useForm();
  const [certTypeModal, setCertTypeModal] = useState<{ open: boolean; rec: any }>({ open: false, rec: null });
  const [workerCertModal, setWorkerCertModal] = useState<{ open: boolean; rec: any }>({ open: false, rec: null });

  const { data: certTypes = [], isLoading: ctLoading } = useCertTypes(cid);
  const { data: workerCertsRes, isLoading: wcLoading } = useWorkerCerts(cid, {});
  const { data: users = [] } = useCCUsers(cid);
  const workerCerts = (workerCertsRes as any)?.data ?? [];

  const cb = (msg: string, closeFn: () => void) => ({ onSuccess: () => { msgApi.success(msg); closeFn(); }, onError: (e: any) => msgApi.error(e?.message || "Error") });

  const ctCreate = useCertTypeCreate(cid, cb("Cert type created.", () => setCertTypeModal({ open: false, rec: null })));
  const ctUpdate = useCertTypeUpdate(cid, cb("Updated.", () => setCertTypeModal({ open: false, rec: null })));
  const ctDelete = useCertTypeDelete(cid, { onSuccess: () => msgApi.success("Deleted."), onError: (e: any) => msgApi.error(e?.message) });

  const wcCreate = useWorkerCertCreate(cid, cb("Cert added.", () => setWorkerCertModal({ open: false, rec: null })));
  const wcUpdate = useWorkerCertUpdate(cid, cb("Updated.", () => setWorkerCertModal({ open: false, rec: null })));
  const wcDelete = useWorkerCertDelete(cid, { onSuccess: () => msgApi.success("Deleted."), onError: (e: any) => msgApi.error(e?.message) });

  function openCTEdit(r?: any) { setCertTypeModal({ open: true, rec: r ?? null }); form.setFieldsValue(r ?? {}); }
  function openWCEdit(r?: any) { setWorkerCertModal({ open: true, rec: r ?? null }); form.setFieldsValue(r ? { ...r, issued_date: r.issued_date ? dayjs(r.issued_date) : null, expiry_date: r.expiry_date ? dayjs(r.expiry_date) : null } : {}); }

  const ctColumns = [
    { title: "Name", dataIndex: "name", key: "n" },
    { title: "Issuing Body", dataIndex: "issuing_body", key: "ib", render: (v: string) => v || "—" },
    { title: "Validity (months)", dataIndex: "validity_months", key: "v", render: (v: number) => v || "—" },
    { title: "Active", dataIndex: "is_active", key: "a", render: (v: number) => <Tag color={v ? "green" : "default"}>{v ? "Yes" : "No"}</Tag> },
    { title: "", key: "act", render: (_: any, r: any) => (
      <Space size="small">
        <Button size="small" onClick={() => openCTEdit(r)}>Edit</Button>
        <Popconfirm title="Delete?" onConfirm={() => ctDelete.mutate({ id: r.id })} okButtonProps={{ danger: true }}><Button size="small" danger>Del</Button></Popconfirm>
      </Space>
    )},
  ];

  const wcColumns = [
    { title: "Worker", dataIndex: "user_name", key: "u" },
    { title: "Cert Type", dataIndex: "cert_type_name", key: "ct" },
    { title: "Cert #", dataIndex: "cert_number", key: "cn", render: (v: string) => v || "—" },
    { title: "Issued", dataIndex: "issued_date", key: "i", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—" },
    { title: "Expiry", dataIndex: "expiry_date", key: "e", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—" },
    { title: "Status", dataIndex: "status", key: "s", render: (v: string) => <Tag color={STATUS_COLOR[v]}>{v?.replace(/_/g, " ")?.toUpperCase()}</Tag> },
    { title: "", key: "act", render: (_: any, r: any) => (
      <Space size="small">
        <Button size="small" onClick={() => openWCEdit(r)}>Edit</Button>
        <Popconfirm title="Delete?" onConfirm={() => wcDelete.mutate({ id: r.id })} okButtonProps={{ danger: true }}><Button size="small" danger>Del</Button></Popconfirm>
      </Space>
    )},
  ];

  return (
    <div>
      {ctx}
      {/* Cert Types */}
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-slate-700 text-sm">Certification Types</h3>
        <Button size="small" type="primary" icon={<Plus size={13} />} onClick={() => openCTEdit()} style={{ background: "#4c1d95" }}>Add Type</Button>
      </div>
      <div className="bg-slate-50 rounded-lg border border-slate-100 mb-5">
        <Table rowKey="id" dataSource={certTypes as any[]} columns={ctColumns} loading={ctLoading} size="small" pagination={false} />
      </div>

      <Divider plain className="my-3">Worker Certifications</Divider>
      <div className="flex justify-end mb-2">
        <Button size="small" type="primary" icon={<Plus size={13} />} onClick={() => openWCEdit()} style={{ background: "#4c1d95" }}>Add Certificate</Button>
      </div>
      <div className="bg-slate-50 rounded-lg border border-slate-100">
        <Table rowKey="id" dataSource={workerCerts} columns={wcColumns} loading={wcLoading} size="small" pagination={{ pageSize: 10 }} />
      </div>

      {/* Cert Type Modal */}
      <Modal open={certTypeModal.open} onCancel={() => setCertTypeModal({ open: false, rec: null })} footer={null} destroyOnClose title={certTypeModal.rec ? "Edit Cert Type" : "New Cert Type"} width={400}>
        <Form form={form} layout="vertical" size="middle" className="pt-2" onFinish={(vals) => {
          if (certTypeModal.rec) ctUpdate.mutate({ id: certTypeModal.rec.id, ...vals });
          else ctCreate.mutate(vals);
        }}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="issuing_body" label="Issuing Body"><Input /></Form.Item>
          <Form.Item name="validity_months" label="Validity (months)"><InputNumber className="w-full" min={1} /></Form.Item>
          <Form.Item name="description" label="Description"><Input.TextArea rows={2} /></Form.Item>
          <div className="flex gap-2 pt-1">
            <Button type="primary" htmlType="submit" style={{ background: "#4c1d95" }} loading={ctCreate.isPending || ctUpdate.isPending}>Save</Button>
            <Button onClick={() => setCertTypeModal({ open: false, rec: null })}>Cancel</Button>
          </div>
        </Form>
      </Modal>

      {/* Worker Cert Modal */}
      <Modal open={workerCertModal.open} onCancel={() => setWorkerCertModal({ open: false, rec: null })} footer={null} destroyOnClose title={workerCertModal.rec ? "Edit Worker Cert" : "Add Worker Certificate"} width={480}>
        <Form form={form} layout="vertical" size="middle" className="pt-2" onFinish={(vals) => {
          const payload = { ...vals, issued_date: vals.issued_date?.format("YYYY-MM-DD"), expiry_date: vals.expiry_date?.format("YYYY-MM-DD") };
          if (workerCertModal.rec) wcUpdate.mutate({ id: workerCertModal.rec.id, ...payload });
          else wcCreate.mutate(payload);
        }}>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="user_id" label="Worker" rules={[{ required: true }]}><Select showSearch options={(users as any[]).map(u => ({ value: u.id, label: `${u.first_name} ${u.last_name}`.trim() || u.email }))} /></Form.Item>
            <Form.Item name="cert_type_id" label="Cert Type" rules={[{ required: true }]}><Select options={(certTypes as any[]).map(c => ({ value: c.id, label: c.name }))} /></Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="cert_number" label="Certificate #"><Input /></Form.Item>
            <Form.Item name="issuing_body" label="Issuing Body"><Input /></Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="issued_date" label="Issued Date" rules={[{ required: true }]}><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
            <Form.Item name="expiry_date" label="Expiry Date"><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
          </div>
          <Form.Item name="document_url" label="Document URL"><Input /></Form.Item>
          {workerCertModal.rec && <Form.Item name="status" label="Status"><Select options={[{ value: "active", label: "Active" }, { value: "expired", label: "Expired" }, { value: "expiring_soon", label: "Expiring Soon" }, { value: "revoked", label: "Revoked" }]} /></Form.Item>}
          <div className="flex gap-2 pt-1">
            <Button type="primary" htmlType="submit" style={{ background: "#4c1d95" }} loading={wcCreate.isPending || wcUpdate.isPending}>Save</Button>
            <Button onClick={() => setWorkerCertModal({ open: false, rec: null })}>Cancel</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
